import { Toaster as SonnerToaster } from 'sonner';
import { useThemeStore } from 'store/theme.store';

/**
 * The status shows in the bare 16px icon's colour — no tinted tile behind it.
 * Never style the toast's `::before` — sonner rewrites it on exit and smears anything painted there.
 */
const STATE_ICON = {
  success: '[&_[data-icon]]:!text-success-600',
  error: '[&_[data-icon]]:!text-error-600',
  warning: '[&_[data-icon]]:!text-warning-500',
  info: '[&_[data-icon]]:!text-info-500',
} as const;

/** `!` is required throughout: sonner injects its stylesheet at runtime and outranks plain utilities. */
const TOAST_CLASS = [
  'group !w-full !items-start !gap-2.5',
  '!rounded-lg !border !border-border !bg-card !py-3.5 !pl-3.5 !pr-11 !text-[13.5px] !text-card-foreground',
  '!shadow-[0_12px_32px_-12px_rgba(16,24,40,0.28),0_2px_8px_-4px_rgba(16,24,40,0.16)]',
  'dark:!shadow-[0_16px_36px_-12px_rgba(0,0,0,0.72),0_2px_8px_-4px_rgba(0,0,0,0.5)]',
].join(' ');

const CLOSE_BUTTON_CLASS = [
  // Sonner parks the close button outside the card's top-left corner; bring it
  // inside, top-right, so nothing hangs off the toast.
  '!left-auto !right-2.5 !top-2.5 !size-6 !transform-none',
  '!rounded-md !border-transparent !bg-transparent !text-muted-foreground !opacity-60',
  'hover:!bg-muted hover:!text-foreground hover:!opacity-100',
  'focus-visible:!opacity-100 focus-visible:!shadow-none focus-visible:!ring-2 focus-visible:!ring-ring',
  '[&>svg]:!size-3.5',
].join(' ');

/** Fire toasts via `toast.success/error/warning/info` from 'components/ui/use-toast'. */
export function Toaster() {
  const theme = useThemeStore((state) => state.theme);

  return (
    <SonnerToaster
      theme={theme}
      position="bottom-right"
      // Under sonner's own 600px breakpoint the container keeps `width: 100%`
      // *and* the left/right offsets, so a full-width toast overhangs the
      // viewport. `w-auto` lets it resolve from the offsets instead.
      className="max-[600px]:!w-auto"
      offset={{ bottom: '24px', right: '16px' }}
      mobileOffset={{
        bottom: 'calc(76px + env(safe-area-inset-bottom))',
        right: '12px',
        left: '12px',
      }}
      visibleToasts={3}
      closeButton
      toastOptions={{
        classNames: {
          toast: TOAST_CLASS,
          icon: '!mx-0 !mt-0.5 !size-4 !items-center !justify-center [&>svg]:!mx-0 [&>svg]:!size-4',
          content: '!min-w-0 !flex-1 !gap-1',
          title: '!text-[13.5px] !font-medium !leading-5 !text-card-foreground',
          description: '!text-[13px] !leading-5 !text-muted-foreground',
          closeButton: CLOSE_BUTTON_CLASS,
          // The action reads as an ink link, not a second lime button.
          actionButton:
            '!h-auto !bg-transparent !px-0 !py-0 !text-[13.5px] !font-semibold !text-foreground !underline !underline-offset-2',
          cancelButton:
            '!rounded-md !bg-muted !px-2.5 !py-1 !text-xs !font-medium !text-muted-foreground',
          ...STATE_ICON,
        },
      }}
    />
  );
}
